import { useCallback, useEffect, useState } from "react";

import { Application } from "@core/Application";
import { Permissions } from "@models/Permissions";
import type { ColaboratorInfo } from "@models/Colaborators";
import type { QontactPointInformation, ShiftRecipeWithUserIDs } from "@models/QontactPoint";
import { useHasPermission } from "@hooks/useHasPermission";
import { ToastManager } from "@shared/designSystem/messages/ToastManager";
import { downloadQontactPointShiftsPdf } from "@components/QontactPoints/QontactPointShiftsPdf";

/** Which shift the modal is editing: null = closed, "new" = Agregar turno */
export type ShiftModalTarget = null | "new" | ShiftRecipeWithUserIDs;

/**
 * Turnos tab (design 2b): shifts of the qontact point, add / edit in the 3c
 * modal, delete with confirmation and "Exportar turnos".
 */
export const useQontactPointTurnosTabViewModel = (orgID: string, qontactPoint: QontactPointInformation) => {
    const canManage = useHasPermission(Permissions.QontactPoints.MANAGE) !== false;

    const [recipes, setRecipes] = useState<ShiftRecipeWithUserIDs[]>([]);
    const [collaborators, setCollaborators] = useState<ColaboratorInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [modalTarget, setModalTarget] = useState<ShiftModalTarget>(null);
    const [recipeToDelete, setRecipeToDelete] = useState<ShiftRecipeWithUserIDs | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const loadRecipes = useCallback(async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const shiftsService = Application.shared().coreDependencies.shiftsService;
            const result = await shiftsService.getShiftRecipesForFunctionalUnit(orgID, qontactPoint.functionalUnitID);
            setRecipes([...(result ?? [])].sort((a, b) => (a.hourFrom ?? "").localeCompare(b.hourFrom ?? "", undefined, { numeric: true })));
        } catch (error) {
            console.error(error);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, [orgID, qontactPoint.functionalUnitID]);

    useEffect(() => {
        void loadRecipes();
    }, [loadRecipes]);

    // Active collaborators, for the "Colaboradores asignados" list and the export
    useEffect(() => {
        let isCancelled = false;
        Application.shared().coreDependencies.usersService.getNomina(orgID)
            .then((result: ColaboratorInfo[]) => {
                if (!isCancelled) {
                    setCollaborators((result ?? []).filter((collaborator) => collaborator.id && !collaborator.deactivationTimestamp));
                }
            })
            .catch((error: unknown) => console.error(error));
        return () => {
            isCancelled = true;
        };
    }, [orgID]);

    const onSaved = (recipe: ShiftRecipeWithUserIDs, isNew: boolean) => {
        setRecipes((current) =>
            isNew
                ? [...current, recipe]
                : current.map((item) => item.firestoreRecordId === recipe.firestoreRecordId ? recipe : item)
        );
    };

    const confirmDelete = async () => {
        if (!recipeToDelete?.firestoreRecordId || isDeleting) {
            return;
        }
        setIsDeleting(true);
        try {
            await Application.shared().coreDependencies.shiftsService.deleteShiftRecipe(
                orgID,
                recipeToDelete.firestoreRecordId,
                qontactPoint.id
            );
            setRecipes((current) => current.filter((item) => item.firestoreRecordId !== recipeToDelete.firestoreRecordId));
            ToastManager.shared().showSuccessMessage("Turno eliminado".localized());
            setRecipeToDelete(null);
        } catch (error) {
            console.error(error);
            ToastManager.shared().showErrorMessage("No se pudo eliminar el turno".localized());
        } finally {
            setIsDeleting(false);
        }
    };

    const exportShifts = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            await downloadQontactPointShiftsPdf({ qontactPointName: qontactPoint.name, recipes, collaborators });
        } catch (error) {
            console.error(error);
            ToastManager.shared().showErrorMessage("No se pudieron exportar los turnos".localized());
        } finally {
            setIsExporting(false);
        }
    };

    return {
        canManage,
        recipes,
        collaborators,
        loading,
        loadError,
        reload: loadRecipes,
        modalTarget,
        openNew: () => setModalTarget("new"),
        openEdit: (recipe: ShiftRecipeWithUserIDs) => setModalTarget(recipe),
        closeModal: () => setModalTarget(null),
        onSaved,
        recipeToDelete,
        askDelete: setRecipeToDelete,
        cancelDelete: () => !isDeleting && setRecipeToDelete(null),
        confirmDelete,
        isDeleting,
        isExporting,
        exportShifts,
    };
};
